import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FiActivity,
  FiEdit2,
  FiEye,
  FiPlus,
  FiRefreshCw,
  FiRotateCw,
  FiTrash2,
} from "react-icons/fi";
import { toast } from "react-toastify";
import BackButton from "../components/BackButton";
import LoadingOverlay from "../components/LoadingOverlay";
import PaginationControls from "../components/PaginationControls";
import {
  deleteAnnouncement,
  listAnnouncements,
  republishAnnouncement,
  updateAnnouncementStatus,
} from "../services/announcementService";
import {
  ANNOUNCEMENT_STATUS_OPTIONS,
  formatAnnouncementDateTime,
  getAnnouncementStatusClasses,
} from "../utils/announcementUtils";

const AnnouncementManagement = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);

    try {
      const response = await listAnnouncements({
        status: statusFilter,
        page: currentPage,
        limit: 10,
        search: searchTerm.trim(),
      });

      setAnnouncements(Array.isArray(response?.data) ? response.data : []);
      setTotalPages(Number(response?.totalPages || 1));
      setTotal(Number(response?.total || 0));
    } catch (error) {
      console.error("Fetch announcements error:", error);
      toast.error(
        error?.response?.data?.message || "โหลดรายการประกาศไม่สำเร็จ"
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleChangeStatus = async (announcement, nextStatus) => {
    const result = await Swal.fire({
      title: `เปลี่ยนสถานะเป็น ${nextStatus}?`,
      text: `ประกาศ "${announcement.title}" จะถูกอัปเดตสถานะ`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#0f766e",
    });

    if (!result.isConfirmed) return;

    try {
      await updateAnnouncementStatus(announcement._id, nextStatus);
      toast.success("อัปเดตสถานะประกาศเรียบร้อย");
      fetchAnnouncements();
    } catch (error) {
      console.error("Update announcement status error:", error);
      toast.error(
        error?.response?.data?.message || "อัปเดตสถานะประกาศไม่สำเร็จ"
      );
    }
  };

  const handleRepublish = async (announcement) => {
    const result = await Swal.fire({
      title: "Republish announcement?",
      text: "Republish this announcement so users can see it again?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Republish",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0f766e",
      background: document.documentElement.classList.contains("dark")
        ? "#1f2937"
        : "#ffffff",
      color: document.documentElement.classList.contains("dark")
        ? "#f9fafb"
        : "#111827",
    });

    if (!result.isConfirmed) return;

    try {
      await republishAnnouncement(announcement._id);
      toast.success("Republished announcement successfully");
      fetchAnnouncements();
    } catch (error) {
      console.error("Republish announcement error:", error);
      toast.error(
        error?.response?.data?.message || "Republish announcement failed"
      );
    }
  };

  const handleDelete = async (announcement) => {
    const result = await Swal.fire({
      title: "Delete announcement permanently?",
      text: "This will permanently remove the announcement and all of its logs.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      background: document.documentElement.classList.contains("dark")
        ? "#1f2937"
        : "#ffffff",
      color: document.documentElement.classList.contains("dark")
        ? "#f9fafb"
        : "#111827",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteAnnouncement(announcement._id);
      toast.success("Deleted announcement successfully");
      fetchAnnouncements();
    } catch (error) {
      console.error("Delete announcement error:", error);
      toast.error(
        error?.response?.data?.message || "Delete announcement failed"
      );
    }
  };

  return (
    <div className="relative min-h-screen bg-gray-50 px-4 py-6 transition-colors duration-300 dark:bg-gray-900 sm:px-6">
      <LoadingOverlay loading={loading} />

      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <BackButton label="ย้อนกลับ" />
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-[26px] font-semibold text-gray-900 dark:text-gray-100">
              Announcement Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              จัดการประกาศระบบสำหรับ popup และตรวจสอบประวัติการตอบรับของผู้ใช้
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/announcement-management/create")}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#00ADB5] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#01969D]"
          >
            <FiPlus size={16} />
            สร้างประกาศใหม่
          </button>
        </div>
      </div>

      <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="ค้นหาจาก title หรือ message"
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 md:max-w-md"
            />

            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setCurrentPage(1);
              }}
              className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            >
              <option value="">ทุกสถานะ</option>
              {ANNOUNCEMENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={fetchAnnouncements}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            <FiRefreshCw size={15} />
            รีเฟรช
          </button>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-100 px-5 py-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          ทั้งหมด {total} รายการ
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600 dark:bg-gray-900/60 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Priority</th>
                <th className="px-4 py-3 font-semibold">Display</th>
                <th className="px-4 py-3 font-semibold">Start</th>
                <th className="px-4 py-3 font-semibold">End</th>
                <th className="px-4 py-3 font-semibold">Updated</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.length > 0 ? (
                announcements.map((announcement) => (
                  <tr
                    key={announcement._id}
                    className="border-t border-gray-100 align-top text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700/40"
                  >
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {announcement.title || "-"}
                      </div>
                      <div className="mt-1 max-w-xs truncate text-xs text-gray-500 dark:text-gray-400">
                        {announcement.message || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium capitalize text-slate-700 dark:bg-slate-700 dark:text-slate-100">
                        {announcement.type || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${getAnnouncementStatusClasses(
                          announcement.status
                        )}`}
                      >
                        {announcement.status || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4">{announcement.priority ?? 0}</td>
                    <td className="px-4 py-4">{announcement.displayMode || "-"}</td>
                    <td className="px-4 py-4">
                      {formatAnnouncementDateTime(announcement.startAt)}
                    </td>
                    <td className="px-4 py-4">
                      {formatAnnouncementDateTime(announcement.endAt)}
                    </td>
                    <td className="px-4 py-4">
                      {formatAnnouncementDateTime(announcement.updatedAt)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex min-w-[240px] flex-wrap gap-2">
                        <ActionButton
                          icon={<FiEye size={14} />}
                          label="View"
                          onClick={() =>
                            navigate(`/announcement-management/${announcement._id}`)
                          }
                        />
                        <ActionButton
                          icon={<FiEdit2 size={14} />}
                          label="Edit"
                          onClick={() =>
                            navigate(
                              `/announcement-management/${announcement._id}/edit`
                            )
                          }
                        />
                        <ActionButton
                          icon={<FiActivity size={14} />}
                          label="Logs"
                          onClick={() =>
                            navigate(
                              `/announcement-management/${announcement._id}/logs`
                            )
                          }
                        />
                        <ActionButton
                          icon={<FiRotateCw size={14} />}
                          label="Republish"
                          onClick={() => handleRepublish(announcement)}
                        />
                        <DangerButton
                          icon={<FiTrash2 size={14} />}
                          label="Delete"
                          onClick={() => handleDelete(announcement)}
                        />

                        {announcement.status !== "active" && (
                          <StatusButton
                            label="Activate"
                            onClick={() =>
                              handleChangeStatus(announcement, "active")
                            }
                          />
                        )}
                        {announcement.status !== "inactive" && (
                          <StatusButton
                            label="Inactivate"
                            onClick={() =>
                              handleChangeStatus(announcement, "inactive")
                            }
                          />
                        )}
                        {announcement.status !== "expired" && (
                          <StatusButton
                            label="Expire"
                            onClick={() =>
                              handleChangeStatus(announcement, "expired")
                            }
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    ไม่พบประกาศตามเงื่อนไขที่ค้นหา
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

const ActionButton = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
  >
    {icon}
    {label}
  </button>
);

const StatusButton = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
  >
    {label}
  </button>
);

const DangerButton = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 transition hover:bg-rose-100 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-900/40"
  >
    {icon}
    {label}
  </button>
);

export default AnnouncementManagement;
